import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { Contact, Group } from '@/types';
import { normalizeBrazilianPhone, BRAZIL_DDDS, DEFAULT_DDD } from '@/lib/brazilDDDs';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string; error_description?: string }) => void;
            error_callback?: (error: any) => void;
          }) => {
            requestAccessToken: (config?: { prompt?: string }) => void;
          };
        };
      };
    };
    __gisTokenClientReady?: boolean;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface ImportableItem {
  resourceName: string;
  name: string;
  phone: string;
  email: string;
  company: string;
  selected: boolean;
}

type Stage = 'intro' | 'connecting' | 'loading' | 'select' | 'importing' | 'done' | 'error';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImport: (contacts: Contact[]) => Promise<void>;
  existingContacts: Contact[];
  groups: Group[];
}

const ENV_CLIENT_ID = import.meta.env.VITE_PUBLIC_GOOGLE_CLIENT_ID as string;
const SCOPES = 'https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/contacts.other.readonly';
const GOOGLE_CLOUD_CREDENTIALS_URL = 'https://console.cloud.google.com/apis/credentials';

export default function GoogleContactsModal({ isOpen, onClose, onImport, existingContacts, groups }: Props) {
  const { profile } = useAuth();
  const [stage, setStage] = useState<Stage>('intro');
  const [items, setItems] = useState<ImportableItem[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [errorIsOriginMismatch, setErrorIsOriginMismatch] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [activeClientId, setActiveClientId] = useState(ENV_CLIENT_ID || '');
  const [checkingConfig, setCheckingConfig] = useState(true);
  const [usingManualPopup, setUsingManualPopup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [googleStats, setGoogleStats] = useState<{ withEmail: number; withCompany: number; withPhone: number; connectionsCount: number; otherContactsCount: number } | null>(null);
  // DDD brasileiro para normalização de telefones
  const [ddd, setDdd] = useState(DEFAULT_DDD);
  const mountedRef = useRef(true);
  const peopleApiInProgressRef = useRef(false);
  const tokenClientRef = useRef<any>(null);
  const gisInitAttemptedRef = useRef(false);
  const popupRef = useRef<Window | null>(null);
  const popupCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messageHandlerBoundRef = useRef(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStage('intro');
      setItems([]);
      setErrorMsg('');
      setErrorDetail('');
      setErrorIsOriginMismatch(false);
      setImportedCount(0);
      setUsingManualPopup(false);
      setGoogleStats(null);
      setSelectedGroup('');
      setDdd(DEFAULT_DDD);
      peopleApiInProgressRef.current = false;
    }
  }, [isOpen]);

  // Fetch contacts via Supabase Edge Function (no CORS issues)
  const fetchViaEdgeFunction = useCallback(async (accessToken: string) => {
    if (!mountedRef.current) return;
    if (peopleApiInProgressRef.current) return;
    peopleApiInProgressRef.current = true;

    // Close manual popup if open
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    }

    setStage('loading');
    setErrorMsg('');
    setErrorDetail('');
    setErrorIsOriginMismatch(false);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const sbToken = sessionData?.session?.access_token || '';

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-google-contacts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sbToken}`,
          },
          body: JSON.stringify({ googleToken: accessToken, ddd }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        const hint = result.hint || '';
        const fullMsg = hint ? `${result.message}\n\n${hint}` : (result.message || `Erro ${response.status} ao buscar contatos`);
        throw new Error(fullMsg);
      }

      const googleContacts: { resourceName: string; name: string; phone: string; email: string; company: string }[] = result.contacts || [];

      // Show non-blocking warning if otherContacts failed but connections succeeded
      if (result.warning) {
        console.warn('Google import warning:', result.warning);
      }

      // Store stats for display
      if (result.stats) {
        setGoogleStats(result.stats);
      }

      if (!mountedRef.current) return;

      // Filter out contacts that already exist in WZAP
      const existingPhones = new Set(existingContacts.map((c) => c.phone.replace(/\D/g, '')));
      const newItems = googleContacts
        .filter((c) => !existingPhones.has(c.phone))
        .map((c) => ({
          resourceName: c.resourceName,
          name: c.name,
          phone: c.phone,
          email: c.email,
          company: c.company,
          selected: true,
        }));

      if (newItems.length === 0) {
        setErrorMsg('Nenhum contato com telefone encontrado');
        setErrorDetail('Sua conta Google não possui contatos com número de telefone, ou todos os contatos já existem na sua lista do WZAP.');
        setStage('error');
        return;
      }

      setItems(newItems);
      setStage('select');
    } catch (err: any) {
      if (!mountedRef.current) return;
      setErrorMsg(err?.message || 'Erro ao buscar contatos do Google.');
      setErrorDetail('Verifique sua conexão e tente novamente. Se o erro persistir, o token pode ter expirado — feche e reabra o modal.');
      setStage('error');
    } finally {
      peopleApiInProgressRef.current = false;
    }
  }, [existingContacts]);

  // Process OAuth token (shared between manual popup and GIS approaches)
  const processOAuthToken = useCallback(async (accessToken: string) => {
    await fetchViaEdgeFunction(accessToken);
  }, [fetchViaEdgeFunction, ddd]);

  // Resume: check for leftover OAuth token in sessionStorage whenever modal opens
  useEffect(() => {
    if (!isOpen) return;

    const token = sessionStorage.getItem('google_oauth_token');
    const timestamp = sessionStorage.getItem('google_oauth_timestamp');
    const error = sessionStorage.getItem('google_oauth_error');

    if (token && timestamp) {
      const elapsed = Date.now() - parseInt(timestamp, 10);
      if (elapsed < 10 * 60 * 1000) {
        sessionStorage.removeItem('google_oauth_token');
        sessionStorage.removeItem('google_oauth_timestamp');
        sessionStorage.removeItem('google_oauth_error');
        sessionStorage.removeItem('google_oauth_error_detail');
        setUsingManualPopup(true);
        processOAuthToken(token);
      } else {
        sessionStorage.removeItem('google_oauth_token');
        sessionStorage.removeItem('google_oauth_timestamp');
      }
    } else if (error) {
      const errDetail = sessionStorage.getItem('google_oauth_error_detail') || '';
      sessionStorage.removeItem('google_oauth_error');
      sessionStorage.removeItem('google_oauth_error_detail');
      setErrorMsg(error);
      setErrorDetail(errDetail);
      setStage('error');
    }
  }, [isOpen, processOAuthToken]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Cleanup popup and interval on unmount
  useEffect(() => {
    return () => {
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }
    };
  }, []);

  // Poll sessionStorage for OAuth token when using manual popup approach
  useEffect(() => {
    if (stage !== 'connecting' || !usingManualPopup) return;

    const poll = setInterval(() => {
      if (!mountedRef.current) {
        clearInterval(poll);
        return;
      }

      const token = sessionStorage.getItem('google_oauth_token');
      const error = sessionStorage.getItem('google_oauth_error');

      if (token) {
        sessionStorage.removeItem('google_oauth_token');
        sessionStorage.removeItem('google_oauth_timestamp');
        clearInterval(poll);
        processOAuthToken(token);
        return;
      }

      if (error) {
        const errDetail = sessionStorage.getItem('google_oauth_error_detail') || '';
        sessionStorage.removeItem('google_oauth_error');
        sessionStorage.removeItem('google_oauth_error_detail');
        clearInterval(poll);
        setErrorMsg(error);
        setErrorDetail(errDetail || 'Tente novamente. Verifique se a URI de redirecionamento está configurada no Google Cloud Console.');
        setStage('error');
        setUsingManualPopup(false);
        return;
      }

      if (popupRef.current && popupRef.current.closed) {
        clearInterval(poll);
        popupRef.current = null;
        setErrorMsg('Popup fechado antes de concluir');
        setErrorDetail(
          'O popup de autorização foi fechado.\n\n' +
          'Possíveis causas:\n' +
          '• Você fechou a janela antes de autorizar\n' +
          '• O Google mostrou um erro (app em modo de teste, email não autorizado, redirect_uri_mismatch)\n\n' +
          'Dica: se você viu uma tela de erro do Google antes de fechar, anote o código do erro para ajudar no diagnóstico.'
        );
        setStage('error');
        setUsingManualPopup(false);
      }
    }, 500);

    return () => clearInterval(poll);
  }, [stage, usingManualPopup, processOAuthToken]);

  // Fetch Client ID from Supabase as fallback
  useEffect(() => {
    if (!isOpen) return;
    if (ENV_CLIENT_ID) {
      setActiveClientId(ENV_CLIENT_ID);
      setCheckingConfig(false);
      return;
    }
    const fetchFromSupabase = async () => {
      if (!profile?.company_id) {
        setCheckingConfig(false);
        return;
      }
      const { data } = await supabase
        .from('whatsapp_configs')
        .select('settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      if (data?.settings && typeof data.settings === 'object') {
        const s = data.settings as Record<string, unknown>;
        if (typeof s.google_oauth_client_id === 'string' && s.google_oauth_client_id) {
          setActiveClientId(s.google_oauth_client_id);
        }
      }
      setCheckingConfig(false);
    };
    fetchFromSupabase();
  }, [isOpen, profile?.company_id]);

  // Listen for postMessage from callback page (manual popup approach)
  useEffect(() => {
    if (messageHandlerBoundRef.current) return;
    messageHandlerBoundRef.current = true;

    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'GOOGLE_OAUTH_SUCCESS' && event.data.accessToken) {
        processOAuthToken(event.data.accessToken as string);
      } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
        if (!mountedRef.current) return;
        const err = event.data.error || 'unknown';
        const desc = event.data.errorDescription || '';
        if (err === 'access_denied' || desc.includes('access_denied')) {
          setErrorMsg('Acesso negado pelo Google');
          setErrorDetail('Você precisa autorizar o acesso aos contatos para continuar. Tente novamente e clique em "Permitir" na tela do Google.');
        } else if (err.includes('redirect_uri_mismatch') || desc.includes('redirect_uri_mismatch')) {
          const callbackUri = `${window.location.origin}/auth/google-callback`;
          setErrorMsg('URI de redirecionamento não autorizada');
          setErrorDetail(`Adicione esta URI exata no Google Cloud Console:\n\n${callbackUri}\n\nVá em APIs & Services → Credentials → seu OAuth Client ID → Authorized redirect URIs.`);
          setErrorIsOriginMismatch(true);
        } else {
          setErrorMsg('Erro na autorização Google');
          setErrorDetail(desc || `Código do erro: ${err}.`);
        }
        setStage('error');
        setUsingManualPopup(false);
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
          popupRef.current = null;
        }
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      messageHandlerBoundRef.current = false;
    };
  }, [processOAuthToken]);

  // Initialize Google GIS token client (fallback approach)
  useEffect(() => {
    const clientId = activeClientId;
    if (!clientId) return;
    if (gisInitAttemptedRef.current) return;
    if (usingManualPopup) return;

    const tryInit = () => {
      if (window.google?.accounts?.oauth2) {
        try {
          tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: SCOPES,
            callback: async (response) => {
              if (!mountedRef.current) return;

              if (response.error) {
                const err = response.error || '';
                const desc = response.error_description || '';

                if (err.includes('origin_mismatch') || desc.includes('origin_mismatch')) {
                  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'desconhecido';
                  setErrorMsg('Origem não autorizada no Google Cloud Console');
                  setErrorDetail(`O domínio "${currentOrigin}" não está na lista de "Authorized JavaScript origins" do seu OAuth Client ID.\n\nPara corrigir:\n1. Acesse o Google Cloud Console → APIs & Services → Credentials\n2. Encontre o Client ID "${clientId.substring(0, 25)}..."\n3. Em "Authorized JavaScript origins", adicione exatamente:\n   ${currentOrigin}\n4. Salve e tente novamente em alguns minutos.`);
                  setErrorIsOriginMismatch(true);
                } else if (err === 'popup_closed_by_user' || err === 'user_cancelled' || desc.includes('popup_closed')) {
                  setErrorMsg('Popup fechado antes de concluir');
                  setErrorDetail('O popup de autorização foi fechado. Tente novamente e autorize o acesso. Se o popup sumiu sozinho, tente a opção "Abrir popup manual".');
                } else if (err === 'popup_blocked_by_browser') {
                  setErrorMsg('Popup bloqueado pelo navegador');
                  setErrorDetail('Seu navegador bloqueou o popup do Google. Permita popups para este site nas configurações do navegador e tente novamente, ou use a opção "Abrir popup manual".');
                } else if (err === 'access_denied' || desc.includes('access_denied')) {
                  setErrorMsg('Acesso negado');
                  setErrorDetail('Você precisa autorizar o acesso. Tente novamente e clique em "Permitir" na tela do Google. Se você for o desenvolvedor, verifique se seu email está na lista de test users no OAuth consent screen.');
                } else {
                  setErrorMsg(err || 'Erro na autorização Google');
                  setErrorDetail(desc || `Código do erro: ${err}. Verifique se o Client ID está correto e se todas as configurações do Google Cloud Console estão completas.`);
                }
                setStage('error');
                return;
              }

              const accessToken = response.access_token;
              if (!accessToken) {
                setErrorMsg('Token de acesso não recebido');
                setErrorDetail('O Google não retornou um token de acesso. Tente novamente.');
                setStage('error');
                return;
              }

              await processOAuthToken(accessToken);
            },
            error_callback: (error: any) => {
              if (!mountedRef.current) return;
              const msg = error?.message || error?.toString() || '';
              if (msg.includes('popup') || msg.includes('blocked') || msg.includes('closed')) {
                setErrorMsg('Popup bloqueado ou fechado');
                setErrorDetail('Seu navegador pode estar bloqueando popups. Tente a opção "Abrir popup manual" abaixo ou permita popups para este site.');
              } else {
                setErrorMsg('Erro ao abrir popup do Google');
                setErrorDetail(msg || 'Verifique se os popups estão liberados no seu navegador. Se o erro persistir, tente a opção "Abrir popup manual".');
              }
              setStage('error');
            },
          });
          gisInitAttemptedRef.current = true;
        } catch {
          setTimeout(tryInit, 500);
        }
      } else {
        setTimeout(tryInit, 300);
      }
    };

    tryInit();
  }, [activeClientId, usingManualPopup, processOAuthToken]);

  // PRIMARY: Manual popup approach
  const handleConnectManual = () => {
    const clientId = activeClientId;
    if (!clientId) {
      setErrorMsg('Google Client ID não configurado');
      setErrorDetail('Adicione VITE_PUBLIC_GOOGLE_CLIENT_ID no arquivo .env ou configure nas Configurações -> Google OAuth.');
      setStage('error');
      return;
    }

    setStage('connecting');
    setErrorMsg('');
    setErrorDetail('');
    setErrorIsOriginMismatch(false);
    setUsingManualPopup(true);

    const redirectUri = `${window.location.origin}/auth/google-callback`;
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=token` +
      `&scope=${encodeURIComponent(SCOPES)}` +
      `&prompt=consent` +
      `&include_granted_scopes=true`;

    const popup = window.open(
      authUrl,
      'googleOAuthPopup',
      'width=500,height=650,left=' + ((window.screen.width - 500) / 2) + ',top=' + ((window.screen.height - 650) / 3),
    );

    if (!popup || popup.closed) {
      setErrorMsg('Popup bloqueado pelo navegador');
      setErrorDetail(
        'Seu navegador bloqueou o popup de autorização do Google.\n\n' +
        'Soluções:\n' +
        '1. Clique no ícone de popup bloqueado na barra de endereço e permita para este site\n' +
        '2. Ou desative temporariamente o bloqueador de popups nas configurações do navegador\n' +
        '3. Ou tente com outro navegador (Chrome funciona melhor)\n\n' +
        'Depois de permitir, clique em "Tentar novamente".'
      );
      setStage('error');
      setUsingManualPopup(false);
      return;
    }

    popupRef.current = popup;
  };

  // FALLBACK: Google GIS approach
  const handleConnectGis = () => {
    const clientId = activeClientId;
    if (!clientId) {
      setErrorMsg('Google Client ID não configurado');
      setErrorDetail('Adicione VITE_PUBLIC_GOOGLE_CLIENT_ID no arquivo .env ou configure nas Configurações -> Google OAuth.');
      setStage('error');
      return;
    }

    if (!tokenClientRef.current) {
      setErrorMsg('Google Identity Services ainda não carregou');
      setErrorDetail('A biblioteca do Google ainda está carregando. Aguarde alguns segundos e tente novamente.');
      setStage('error');
      return;
    }

    setStage('connecting');
    setErrorMsg('');
    setErrorDetail('');
    setErrorIsOriginMismatch(false);
    setUsingManualPopup(false);

    tokenClientRef.current.requestAccessToken({ prompt: '' });
  };

  const toggleItem = (resourceName: string) => {
    setItems((prev) =>
      prev.map((it) => (it.resourceName === resourceName ? { ...it, selected: !it.selected } : it)),
    );
  };

  const toggleAll = () => {
    const allSelected = items.every((it) => it.selected);
    setItems((prev) => prev.map((it) => ({ ...it, selected: !allSelected })));
  };

  const handleImport = async () => {
    const selected = items.filter((it) => it.selected);
    if (selected.length === 0) return;

    setStage('importing');
    setErrorMsg('');
    setErrorDetail('');

    const now = new Date().toISOString();
    const newContacts: Contact[] = selected.map((it, i) => ({
      id: `gc${Date.now()}-${i}`,
      name: it.name,
      phone: it.phone,
      email: it.email || null,
      company_name: it.company || null,
      city: null,
      state: null,
      tags: ['google-import'],
      notes: null,
      group_id: selectedGroup || null,
      created_at: now,
    }));

    try {
      await onImport(newContacts);
      if (!mountedRef.current) return;
      setImportedCount(newContacts.length);
      setStage('done');
    } catch (err: any) {
      if (!mountedRef.current) return;
      setErrorMsg('Erro ao importar contatos');
      setErrorDetail(err?.message || 'Ocorreu um erro ao salvar os contatos. Verifique sua conexão e tente novamente.');
      setStage('error');
    }
  };

  const resetAndClose = () => {
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    }
    setStage('intro');
    setItems([]);
    setErrorMsg('');
    setErrorDetail('');
    setErrorIsOriginMismatch(false);
    setImportedCount(0);
    setUsingManualPopup(false);
    setSelectedGroup('');
    setGoogleStats(null);
    peopleApiInProgressRef.current = false;
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = items.filter((it) => it.selected).length;
  const allSelected = items.length > 0 && items.every((it) => it.selected);
  const someSelected = items.some((it) => it.selected) && !allSelected;

  const selectedGroupName = selectedGroup ? (groups.find((g) => g.id === selectedGroup)?.name || selectedGroup) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={resetAndClose} />

      <div className="relative w-full max-w-[620px] max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-background-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-background-200 flex items-center justify-center shadow-sm">
              <i className="ri-google-line text-xl text-foreground-600" />
            </div>
            <div>
              <h3 className="text-base font-heading font-bold text-foreground-950">Importar do Google Contatos</h3>
              <p className="text-xs text-foreground-500">
                {stage === 'intro' && 'Conecte sua conta Google para importar'}
                {stage === 'connecting' && 'Abrindo autorização do Google...'}
                {stage === 'loading' && 'Buscando seus contatos...'}
                {stage === 'select' && `${items.length} contatos encontrados`}
                {stage === 'importing' && 'Importando...'}
                {stage === 'done' && 'Importação concluída'}
                {stage === 'error' && 'Erro na conexão'}
              </p>
            </div>
          </div>
          <button
            onClick={resetAndClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* INTRO */}
          {stage === 'intro' && (
            <div className="space-y-5">
              {checkingConfig ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !activeClientId ? (
                <div className="flex flex-col items-center py-8">
                  <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4 border border-amber-200">
                    <i className="ri-settings-3-line text-2xl text-amber-600" />
                  </div>
                  <p className="text-sm font-semibold text-foreground-900 mb-1">Client ID não configurado</p>
                  <p className="text-xs text-foreground-500 text-center max-w-[350px] mb-4">
                    O Google Client ID precisa ser configurado para usar a importação de contatos.
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href="/settings/google-oauth"
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Configurar Google OAuth
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-50 border border-background-200">
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-background-100">
                      <i className="ri-google-line text-2xl text-foreground-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Importação direta do Google</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Conecte sua conta Google e importe seus contatos com apenas um clique.
                        O WZAP irá buscar nome, telefone e email dos seus contatos salvos no Google.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Como funciona</h4>
                    {[
                      { icon: 'ri-shield-check-line', text: 'Você autoriza o acesso somente leitura aos seus contatos' },
                      { icon: 'ri-filter-line', text: 'O WZAP filtra automaticamente contatos sem telefone e duplicados' },
                      { icon: 'ri-checkbox-circle-line', text: 'Você escolhe quais contatos deseja importar' },
                      { icon: 'ri-price-tag-3-line', text: 'Os contatos importados recebem a tag "google-import"' },
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <i className={`${step.icon} text-emerald-600 text-sm`} />
                        </div>
                        <p className="text-sm text-foreground-700 pt-0.5">{step.text}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 rounded-xl bg-primary-50 border border-primary-200">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-primary-600 text-lg flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-primary-800 mb-1">Pré-requisitos no Google Cloud Console</p>
                        <p className="text-xs text-primary-700 leading-relaxed mb-2">
                          Verifique se tudo está configurado corretamente:
                        </p>
                        <div className="space-y-2.5">
                          <div>
                            <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">1. Authorized JavaScript origins</span>
                            <code className="block mt-0.5 px-3 py-1.5 bg-white rounded-lg text-xs font-mono text-primary-700 border border-primary-200 break-all">
                              {typeof window !== 'undefined' ? window.location.origin : 'https://seu-dominio.com'}
                            </code>
                          </div>
                          <div>
                            <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">2. Authorized redirect URIs</span>
                            <code className="block mt-0.5 px-3 py-1.5 bg-white rounded-lg text-xs font-mono text-primary-700 border border-primary-200 break-all">
                              {typeof window !== 'undefined' ? `${window.location.origin}/auth/google-callback` : 'https://seu-dominio.com/auth/google-callback'}
                            </code>
                          </div>
                          <div>
                            <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">3. OAuth consent screen</span>
                            <p className="text-xs text-primary-700 mt-0.5">
                              App em <strong>Testing</strong>? Adicione seu email em <strong>Test users</strong>.
                              App em <strong>Production</strong>? Precisa ser verificado pelo Google.
                            </p>
                          </div>
                          <div>
                            <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">4. Google People API</span>
                            <p className="text-xs text-primary-700 mt-0.5">
                              A API precisa estar <strong>ativada</strong> no projeto.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <a
                            href={GOOGLE_CLOUD_CREDENTIALS_URL}
                            target="_blank"
                            rel="nofollow"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            <i className="ri-external-link-line" />
                            Abrir Google Cloud Console
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleConnectManual}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm rounded-xl transition-all cursor-pointer shadow-sm"
                  >
                    <i className="ri-google-line text-xl" />
                    Conectar com Google (recomendado)
                  </button>

                  <button
                    onClick={handleConnectGis}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 font-medium text-xs rounded-xl transition-all cursor-pointer"
                  >
                    Tentar com Google Identity Services (alternativo)
                  </button>

                  <p className="text-[11px] text-foreground-400 text-center">
                    Um popup será aberto para você autorizar o acesso aos seus contatos do Google.
                    Se o popup for bloqueado, permita popups para este site nas configurações do navegador.
                  </p>
                </>
              )}
            </div>
          )}

          {/* CONNECTING */}
          {stage === 'connecting' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-full border-[3px] border-background-200 border-t-primary-500 animate-spin mb-5" />
              <p className="text-sm font-medium text-foreground-700">Aguardando autorização do Google...</p>
              <p className="text-xs text-foreground-400 mt-1">Verifique se o popup não foi bloqueado pelo navegador</p>
              <p className="text-xs text-foreground-300 mt-3">Se nada acontecer, feche esta janela e tente novamente.</p>
            </div>
          )}

          {/* LOADING */}
          {stage === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-full border-[3px] border-background-200 border-t-primary-500 animate-spin mb-5" />
              <p className="text-sm font-medium text-foreground-700">Buscando seus contatos do Google...</p>
              <p className="text-xs text-foreground-400 mt-1">Isso pode levar alguns segundos</p>
            </div>
          )}

          {/* SELECT */}
          {stage === 'select' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-foreground-600">
                  <strong className="text-foreground-900">{items.length}</strong> contatos encontrados
                  <span className="text-foreground-400"> (duplicados com sua lista atual foram removidos)</span>
                </p>
                <label className="flex items-center gap-2 text-sm text-foreground-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                  />
                  Selecionar todos
                </label>
              </div>

              {/* DDD selector */}
              <div className="p-4 rounded-xl bg-white border border-background-200 space-y-2">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-primary-600 text-lg" />
                  <label className="text-sm font-semibold text-foreground-900">DDD (Código de Área) do Brasil</label>
                </div>
                <select
                  value={ddd}
                  onChange={(e) => setDdd(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border-2 border-background-200 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer font-medium"
                >
                  {BRAZIL_DDDS.map((d) => (
                    <option key={d.code} value={d.code}>{d.code} — {d.state} ({d.region})</option>
                  ))}
                  <option value="">Nenhum (manter como está)</option>
                </select>
                <p className="text-xs text-foreground-400">
                  {ddd
                    ? <>Números sem DDD recebem <strong>55 (Brasil) + DDD {ddd}</strong> automaticamente. Números com DDD mas sem 55 recebem <strong>55</strong>.</>
                    : <>Os números serão importados exatamente como estão no Google Contatos</>}
                </p>
              </div>

              {/* Group selector — MOVED TO TOP for visibility */}
              {groups.length > 0 && (
                <div className="p-4 rounded-xl bg-white border-2 border-dashed border-primary-300 space-y-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-group-line text-primary-600 text-lg" />
                    <label className="text-sm font-semibold text-foreground-900">Selecionar grupo de destino</label>
                    <span className="text-xs text-red-500 font-medium">*</span>
                  </div>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border-2 border-background-200 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer font-medium"
                  >
                    <option value="">— Escolha um grupo (obrigatório) —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  {selectedGroupName && (
                    <div className="flex items-center gap-2 text-xs text-primary-700 bg-primary-50 rounded-lg px-3 py-2">
                      <i className="ri-checkbox-circle-fill text-primary-500" />
                      <span>
                        <strong>{selectedCount}</strong> contato{selectedCount !== 1 ? 's' : ''} serão importados no grupo <strong>"{selectedGroupName}"</strong>
                      </span>
                    </div>
                  )}
                  {!selectedGroup && (
                    <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                      <i className="ri-alert-line" />
                      <span>Selecione um grupo acima antes de importar. Contatos sem grupo ficam difíceis de encontrar depois.</span>
                    </div>
                  )}
                </div>
              )}

              {/* Stats banner */}
              {googleStats && (
                <div className="flex items-center gap-4 p-3 rounded-xl bg-background-50 border border-background-200 text-xs text-foreground-600 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-phone-line text-foreground-400" />
                    {googleStats.withPhone} com telefone
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-mail-line text-foreground-400" />
                    {googleStats.withEmail} com email
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-building-line text-foreground-400" />
                    {googleStats.withCompany} com empresa
                  </span>
                  {googleStats.otherContactsCount > 0 && (
                    <span className="flex items-center gap-1.5 text-emerald-600">
                      <i className="ri-check-line" />
                      {googleStats.otherContactsCount} de "Outros contatos" incluídos
                    </span>
                  )}
                </div>
              )}

              {/* Low data warning */}
              {googleStats && googleStats.withPhone > 0 && googleStats.withEmail === 0 && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  <i className="ri-alert-line flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-0.5">Nenhum email encontrado nos contatos do Google</p>
                    <p className="text-amber-600">Os contatos importados da sua conta Google não possuem endereços de email salvos. Verifique se seus contatos no Google têm emails cadastrados.</p>
                  </div>
                </div>
              )}
              {googleStats && googleStats.withPhone > 0 && googleStats.withCompany === 0 && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  <i className="ri-alert-line flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-0.5">Nenhuma empresa encontrada nos contatos do Google</p>
                    <p className="text-amber-600">Os contatos importados não possuem nomes de empresa salvos no Google Contatos.</p>
                  </div>
                </div>
              )}

              <div className="max-h-[260px] overflow-y-auto rounded-xl border border-background-200 divide-y divide-background-100">
                {items.map((item) => (
                  <label
                    key={item.resourceName}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={() => toggleItem(item.resourceName)}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">{item.name}</p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="text-xs text-foreground-500 font-mono">{item.phone}</span>
                        {item.email ? (
                          <span className="text-xs text-emerald-600 truncate max-w-[200px] flex items-center gap-1">
                            <i className="ri-mail-line text-[10px]" />
                            {item.email}
                          </span>
                        ) : (
                          <span className="text-xs text-foreground-300 flex items-center gap-1">
                            <i className="ri-mail-line text-[10px]" />
                            sem email
                          </span>
                        )}
                        {item.company ? (
                          <span className="text-xs text-emerald-600 truncate max-w-[180px] flex items-center gap-1">
                            <i className="ri-building-line text-[10px]" />
                            {item.company}
                          </span>
                        ) : (
                          <span className="text-xs text-foreground-300 flex items-center gap-1">
                            <i className="ri-building-line text-[10px]" />
                            sem empresa
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <button
                onClick={handleImport}
                disabled={selectedCount === 0}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-400 text-white font-semibold text-sm rounded-xl transition-colors cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-download-line text-lg" />
                Importar {selectedCount} contato{selectedCount !== 1 ? 's' : ''}
                {selectedGroupName ? ` no grupo "${selectedGroupName}"` : ' (sem grupo selecionado!)'}
              </button>
            </div>
          )}

          {/* IMPORTING */}
          {stage === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-full border-[3px] border-background-200 border-t-primary-500 animate-spin mb-5" />
              <p className="text-sm font-medium text-foreground-700">Importando contatos...</p>
              <p className="text-xs text-foreground-400 mt-1">Adicionando a sua lista do WZAP</p>
            </div>
          )}

          {/* DONE */}
          {stage === 'done' && (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <i className="ri-check-line text-3xl text-emerald-600" />
              </div>
              <h4 className="text-lg font-heading font-bold text-foreground-950 mb-1">Importação concluída!</h4>
              <p className="text-sm text-foreground-600 mb-2">
                <strong>{importedCount}</strong> contato{importedCount !== 1 ? 's' : ''} importado{importedCount !== 1 ? 's' : ''} com sucesso
              </p>
              {selectedGroupName && (
                <p className="text-xs text-primary-600 mb-4">
                  <i className="ri-group-line mr-1" />
                  Adicionados ao grupo <strong>"{selectedGroupName}"</strong>
                </p>
              )}
              <p className="text-xs text-foreground-400 mb-6">Tag &ldquo;google-import&rdquo; aplicada automaticamente</p>
              <button
                onClick={resetAndClose}
                className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Concluir
              </button>
            </div>
          )}

          {/* ERROR */}
          {stage === 'error' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4 border border-red-200">
                <i className="ri-error-warning-line text-3xl text-red-500" />
              </div>
              <h4 className="text-lg font-heading font-bold text-foreground-950 mb-2">{errorMsg}</h4>
              {errorDetail && (
                <div className="w-full p-4 rounded-xl bg-red-50 border border-red-200 mb-6">
                  <p className="text-sm text-red-700 whitespace-pre-line leading-relaxed">{errorDetail}</p>
                </div>
              )}
              {(errorIsOriginMismatch || errorMsg.includes('Google Cloud Console') || errorMsg.includes('URI de redirecionamento')) && (
                <a
                  href={GOOGLE_CLOUD_CREDENTIALS_URL}
                  target="_blank"
                  rel="nofollow"
                  className="inline-flex items-center gap-2 px-4 py-2 mb-4 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line" />
                  Abrir Google Cloud Console
                </a>
              )}
              <div className="flex items-center gap-3 flex-wrap justify-center">
                <button
                  onClick={handleConnectManual}
                  className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Tentar com popup manual
                </button>
                {tokenClientRef.current && (
                  <button
                    onClick={handleConnectGis}
                    className="px-5 py-2.5 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Tentar com Google GIS
                  </button>
                )}
                <button
                  onClick={resetAndClose}
                  className="px-5 py-2.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Fechar
                </button>
              </div>

              {/* Detailed troubleshooting steps */}
              {!errorIsOriginMismatch && (
                <div className="w-full mt-5 p-4 rounded-xl bg-background-50 border border-background-200">
                  <p className="text-xs font-semibold text-foreground-600 mb-3">Possíveis causas e soluções:</p>
                  <div className="space-y-2 text-xs text-foreground-500">
                    <p><strong>1. App em modo Testing:</strong> no Google Cloud Console, vá em OAuth consent screen, adicione seu email em Test users.</p>
                    <p><strong>2. People API não ativada:</strong> vá em APIs &amp; Services &gt; Library, pesquise "Google People API" e ative.</p>
                    <p><strong>3. Popup bloqueado:</strong> verifique o ícone de bloqueio na barra de endereço e permita popups para este site.</p>
                    <p><strong>4. Redirect URI faltando:</strong> adicione <code className="px-1.5 py-0.5 bg-background-100 rounded text-[11px]">{window.location.origin}/auth/google-callback</code> em Authorized redirect URIs.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}